import { inject as service } from '@ember/service';
import { isPresent, typeOf } from '@ember/utils';
import DS from 'ember-data';
import ActiveModelAdapter from 'active-model-adapter';

import SessionService, { SessionData } from 'ember-simple-auth/services/session';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import RouterService from '@ember/routing/router-service';
import { ApiError } from 'cio/services/api';
import EnvironmentService from 'cio/services/environment';
import ReleaseNotificationService from 'cio/services/release-notification';
import { ModelRegistry } from 'ember-data/model';
import { computed } from '@ember/object';
import { getHostForDataCenter } from 'cio/utils/data-centers';
import * as JSONBig from 'json-bigint';
import { errorsHashToArray } from 'cio/utils/ember-data-error-hash-to-array';

const { InvalidError } = DS;

const GLOBAL_MODELS: (keyof ModelRegistry)[] = [
  'account', // resource paths already have account scope: "/v1/accounts/{:id}"
  'environment', // resource paths already have environment scope: "/v1/environments/{:id}
  'integration', // global supported integrations list, no scope: "/v1/integrations"
];

// account scope models:
// "/v1/accounts/:account_id/foo/{:id}"
const ACCOUNT_SCOPE_MODELS: (keyof ModelRegistry)[] = [
  'notification',
  'user',
  'receipt',
  'whitelisted-ip',

  // API credentials and keys: account scope only used for GET paths, where UI doesn't know the env.
  // ID for other requests, specific adapters build URLs in a custom environment scope:
  'api-credential',
  'ext-api-key',
];

// eslint-disable-next-line @typescript-eslint/naming-convention, new-cap
const configuredJSONBig = JSONBig({
  storeAsString: true,
  storeTransformedSafeIntegerAsString: true,
  objectProto: true,
  protoAction: 'ignore',
  constructorAction: 'ignore',
});

export type Payload = Error | Record<string, any> | unknown[] | string | undefined;

export default class ApplicationAdapter extends ActiveModelAdapter {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service releaseNotification!: ReleaseNotificationService;
  @service session!: SessionService;
  @service router!: RouterService;

  namespace = 'v1';

  @computed('account.current.dataCenter')
  get host() {
    return getHostForDataCenter(this.account.current?.dataCenter) ?? '';
  }

  // dependent on Cookie values, which cannot be observed: getter executed every time
  get headers() {
    if (!this.session.isAuthenticated) {
      return {};
    }

    let { accessToken } = (this.session.data as SessionData).authenticated;
    let version = this.releaseNotification.original?.version;
    let headers: { [key: string]: string } = {
      Authorization: `Bearer ${accessToken}`,
    };

    if (isPresent(version)) {
      headers['X-UI-Version'] = version;
    }

    return headers;
  }

  ajax(url: string, type: string, options: any) {
    let originalJsonParse = globalThis.JSON.parse;

    // Temporarily override JSON.parse, as there's no other way to reach
    // into Ember Data's fetch response handler:
    // https://github.com/emberjs/data/blob/c829d9bd900efa68c2372aa1eba58482881abf72/packages/adapter/src/-private/utils/determine-body-promise.ts#L47
    Object.assign(globalThis.JSON, { parse: configuredJSONBig.parse });

    return super.ajax(url, type, options).finally(() => {
      Object.assign(globalThis.JSON, { parse: originalJsonParse });
    });
  }

  pathForType(modelName: keyof ModelRegistry) {
    let path = super.pathForType(modelName);
    let account = this.account.current;
    let env = this.get('environment').get('current');

    // Ignore initial stubbed environment.current object
    // @ts-ignore - typescript thinks typeof returns an object here
    if (
      (typeOf(env) === 'instance' || ('fakePostSignup' in env && env.fakePostSignup === true)) &&
      !GLOBAL_MODELS.includes(modelName) &&
      !ACCOUNT_SCOPE_MODELS.includes(modelName) &&
      env.id != null
    ) {
      let envId = encodeURIComponent(env.id);

      path = `environments/${envId}/${path}`;
    } else if (ACCOUNT_SCOPE_MODELS.includes(modelName)) {
      path = `accounts/${account.id}/${path}`;
    }

    return path;
  }

  shouldReloadRecord() {
    return true;
  }

  shouldBackgroundReloadRecord() {
    return false;
  }

  shouldReloadAll(_store: DS.Store) {
    return true;
  }

  handleResponse(status: number, headers: Record<string, any>, payload: Payload, requestData: Record<string, any>) {
    this.auth.refreshAuthCookies();

    this._handleUnauthorizedResponseConditionalTwoFactorAuthSetupWizardRedirect(status, payload);

    this.auth.checkAccessExpiration();

    if (this.isInvalid(status, headers, payload as any)) {
      let errors = typeof payload === 'object' && 'errors' in payload ? payload.errors : undefined;

      if (!Array.isArray(errors)) {
        errors = errorsHashToArray(errors);
      }

      return new InvalidError(errors);
    } else {
      return super.handleResponse(status, headers, payload as any, requestData);
    }
  }

  _handleUnauthorizedResponseConditionalTwoFactorAuthSetupWizardRedirect(status: number, payload: Payload) {
    // Error is different than 401, or this is a login request (user is not logged-in): don't do anything
    let isAuthenticated = this.session.isAuthenticated || this.auth.isReauthenticating;
    if (status !== 401 || !isAuthenticated) {
      return;
    }
    if (payload != null && typeof payload === 'object') {
      let errors = 'errors' in payload ? payload.errors : [];
      let unauthorizedDueToDisabledTwoFactorAuth = ((errors as ApiError[]) || []).some(
        (err) => err.detail === 'unauthorized: two-factor authentication not enabled',
      );

      // generic unauthorized error: log-out
      if (!unauthorizedDueToDisabledTwoFactorAuth) {
        return this.auth.logout();
      }
    }

    // unauthorized due to disabled 2FA setting, but already on the 2FA setup wizard: don't do anything
    if (this.router.isActive('authenticated.2fa_setup')) {
      return;
    }

    // unauthorized due to disabled 2FA setting: redirect to the 2FA setup wizard
    return this.router.transitionTo('authenticated.2fa_setup');
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your adapters.
declare module 'ember-data/types/registries/adapter' {
  export default interface AdapterRegistry {
    application: ApplicationAdapter;
  }
}
